import { Link, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { 
  Settings, 
  Users, 
  CreditCard, 
  BarChart3, 
  Package,
  HelpCircle,
  Shield,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Car,
  UserCircle,
  Wrench,
  Building,
  DollarSign,
  Cog,
  TrendingUp
} from "lucide-react";
import { useState, useEffect } from "react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";

export function AdminSection() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { hasCompanyRole } = useAuth();

  // Verificar se o usuário é admin da empresa
  const isCompanyAdmin = hasCompanyRole(slug, 'company_admin') || hasCompanyRole(slug, 'admin_company');
  
  if (!isCompanyAdmin) return null;

  const currentPath = location.pathname;
  
  const isActive = (path: string) => {
    const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
    const cleanCurrent = currentPath.endsWith("/") ? currentPath.slice(0, -1) : currentPath;
    return cleanCurrent === cleanPath || cleanCurrent.startsWith(cleanPath + "/");
  };

  const adminNavItems = [
    { 
      title: "Dashboard Admin", 
      url: `/company/${slug}/admin`, 
      icon: BarChart3,
      description: "Visão geral administrativa"
    },
    { 
      title: "Usuários", 
      url: `/company/${slug}/admin/users`, 
      icon: Users,
      description: "Gerenciar usuários da empresa"
    },
    { 
      title: "Planos", 
      url: `/company/${slug}/admin/plans`, 
      icon: Package,
      description: "Gerenciar planos e licenças"
    },
    { 
      title: "Faturamento", 
      url: `/company/${slug}/admin/billing`, 
      icon: CreditCard,
      description: "Faturas e pagamentos"
    },
    { 
      title: "Configurações", 
      url: `/company/${slug}/admin/settings`, 
      icon: Settings,
      description: "Configurações da empresa"
    },
    { 
      title: "Suporte", 
      url: `/company/${slug}/admin/support`, 
      icon: HelpCircle,
      description: "Central de suporte"
    },
  ];

  const reportNavItems = [
    {
      title: "Dashboard",
      url: `/company/${slug}/admin/reports/dashboard`,
      icon: LayoutDashboard,
    },
    {
      title: "Veículos",
      url: `/company/${slug}/admin/reports/vehicles`,
      icon: Car,
    },
    {
      title: "Motoristas",
      url: `/company/${slug}/admin/reports/drivers`,
      icon: UserCircle,
    },
    {
      title: "Manutenção",
      url: `/company/${slug}/admin/reports/maintenance`,
      icon: Wrench,
    },
    {
      title: "Centros de Custo",
      url: `/company/${slug}/admin/reports/cost-centers`,
      icon: Building,
    },
    {
      title: "Financeiro",
      url: `/company/${slug}/admin/reports/financial`,
      icon: DollarSign,
    },
    {
      title: "Operacional",
      url: `/company/${slug}/admin/reports/operational`,
      icon: Cog,
    },
    {
      title: "Comparativo",
      url: `/company/${slug}/admin/reports/comparative`,
      icon: TrendingUp,
    },
  ];

  const [isReportsExpanded, setIsReportsExpanded] = useState(false);

  // Auto-expand reports if on a report page
  useEffect(() => {
    if (currentPath.includes(`/company/${slug}/admin/reports`)) {
      setIsReportsExpanded(true);
    }
  }, [currentPath, slug]);

  return (
    <SidebarGroup className="border-t border-border/50 mt-4 pt-4">
      <SidebarGroupLabel className="flex items-center gap-2 px-4 py-2">
        <div className="flex h-6 w-6 items-center justify-center rounded bg-orange-500">
          <Shield className="h-3 w-3 text-white" />
        </div>
        Administração
        <Badge variant="outline" className="ml-auto text-xs bg-orange-50 text-orange-700 border-orange-200">
          Admin
        </Badge>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {adminNavItems.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton 
                asChild 
                isActive={isActive(item.url)}
                className={cn(
                  "group",
                  isActive(item.url) && "bg-orange-50 text-orange-900 hover:bg-orange-100"
                )}
              >
                <Link 
                  to={item.url}
                  className="flex items-center gap-3 transition-colors"
                >
                  <item.icon className={cn(
                    "h-4 w-4 transition-colors",
                    isActive(item.url) ? "text-orange-600" : "text-muted-foreground group-hover:text-foreground"
                  )} />
                  <span className="truncate">{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}

          {/* Reports Section with Dropdown */}
          <SidebarMenuItem>
            <Collapsible
              open={isReportsExpanded}
              onOpenChange={setIsReportsExpanded}
              className="w-full"
            >
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  className={cn(
                    "group w-full justify-between",
                    currentPath.includes(`/company/${slug}/admin/reports`) && "bg-orange-50 text-orange-900"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 className={cn(
                      "h-4 w-4 transition-colors",
                      currentPath.includes(`/company/${slug}/admin/reports`) ? "text-orange-600" : "text-muted-foreground group-hover:text-foreground"
                    )} />
                    <span className="truncate">Relatórios</span>
                  </div>
                  {isReportsExpanded ? (
                    <ChevronDown className="h-4 w-4 transition-transform" />
                  ) : (
                    <ChevronRight className="h-4 w-4 transition-transform" />
                  )}
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {reportNavItems.map((report) => (
                    <SidebarMenuSubItem key={report.title}>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={isActive(report.url)}
                        className={cn(
                          isActive(report.url) && "bg-orange-50 text-orange-900"
                        )}
                      >
                        <Link to={report.url} className="flex items-center gap-2">
                          <report.icon className="h-4 w-4" />
                          <span>{report.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </Collapsible>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}